import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { RefObject } from 'react';
import type Konva from 'konva';
import { CheckCircle2, RotateCcw, Palette, MoreVertical, X, ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../../components/ui/button';
import type {
  CommentNode,
  CreateReplyPayload,
  CreateRootCommentPayload,
  UpdateCommentPayload,
} from '../../domains/comments/models/Comment';
import { useCommentOverlayStore } from '../../stores/commentOverlayStore';
import type { IEventBus } from '../../core/events/IEventBus';
import { CreateRootCommentCommand } from '../../core/commands/CreateRootCommentCommand';
import type { CommandManager } from '../../core/commands/CommandManager';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { resolveCommentAuthorVisual } from '../../lib/comments/commentAuthor';
import { formatRelativeTime } from '../../lib/datetime/relativeTime';
import { Logger } from '../../utils/logger';
import { useDiagramMentionables } from '../../hooks/comments';
import { renderTokensToDisplay, retokenizeFromMentions } from '../../lib/comments/mentionTokens';
import { useAuthStore } from '../../stores/authStore';

const logger = Logger.getLoggerWithPrefix('CommentOverlays');

const findCommentInTree = (nodes: CommentNode[] | undefined, commentId: string): CommentNode | null => {
  if (!nodes) return null;
  for (const node of nodes) {
    if (node.id === commentId) {
      return node;
    }
    const child = findCommentInTree(node.replies, commentId);
    if (child) {
      return child;
    }
  }
  return null;
};

interface CommentOverlaysProps {
  stageRef: RefObject<Konva.Stage>;
  comments: CommentNode[];
  diagramId: string;
  createRootComment: (input: CreateRootCommentPayload) => Promise<void>;
  createReply: (parentId: string, input: CreateReplyPayload) => Promise<void>;
  isCreating: boolean;
  isReplying: boolean;
  eventBus: IEventBus;
  resolveComment: (commentId: string) => Promise<void>;
  reopenComment: (commentId: string) => Promise<void>;
  isResolving: boolean;
  isReopening: boolean;
  commandManager: CommandManager;
  updateComment: (commentId: string, input: UpdateCommentPayload) => Promise<void>;
  isUpdating: boolean;
  softDeleteComment: (commentId: string) => Promise<void>;
  isDeleting: boolean;
  currentUserId: string;
}

export const CommentOverlays: React.FC<CommentOverlaysProps> = ({
  stageRef,
  comments,
  diagramId,
  createRootComment,
  createReply,
  isCreating,
  isReplying,
  eventBus,
  resolveComment,
  reopenComment,
  isResolving,
  isReopening,
  commandManager,
  updateComment,
  isUpdating,
  softDeleteComment,
  isDeleting,
  currentUserId,
}) => {
  const { t, i18n } = useTranslation('common');
  const timeLocale = i18n.resolvedLanguage ?? i18n.language;
  const prompt = useCommentOverlayStore((state) => state.prompt);
  const draft = useCommentOverlayStore((state) => state.draft);
  const setDraft = useCommentOverlayStore((state) => state.setDraft);
  const closePrompt = useCommentOverlayStore((state) => state.closePrompt);
  const clearActiveComment = useCommentOverlayStore((state) => state.clearActiveComment);
  const activeCommentId = useCommentOverlayStore((state) => state.activeCommentId);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [rootMentions, setRootMentions] = useState<string[]>([]);
  const [replyMentions, setReplyMentions] = useState<string[]>([]);
  const [activeCommentColor, setActiveCommentColor] = useState<string | null>(null);
  const [isColorPopoverOpen, setIsColorPopoverOpen] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const editingTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const promptContainerRef = useRef<HTMLDivElement | null>(null);
  const [overlayCoordinates, setOverlayCoordinates] = useState<{ left: number; top: number } | null>(null);
  const [composerCursor, setComposerCursor] = useState<number>(0);
  const [replyComposerCursor, setReplyComposerCursor] = useState<number>(0);
  const [activeMentionQuery, setActiveMentionQuery] = useState<string | null>(null);
  const [activeReplyMentionQuery, setActiveReplyMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [replyMentionStart, setReplyMentionStart] = useState<number | null>(null);
  type ProtectedRange = { start: number; end: number; id: string };
  const [protectedRanges, setProtectedRanges] = useState<ProtectedRange[]>([]);
  const [replyProtectedRanges, setReplyProtectedRanges] = useState<ProtectedRange[]>([]);
  const [editingProtectedRanges, setEditingProtectedRanges] = useState<ProtectedRange[]>([]);
  const [editingMentions, setEditingMentions] = useState<string[]>([]);
  const [editingMentionQuery, setEditingMentionQuery] = useState<string | null>(null);
  const { data: mentionables = [] } = useDiagramMentionables(diagramId);
  const mentionablesMap = useMemo(() => new Map(mentionables.map((m) => [m.id, m])), [mentionables]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<string | null>(null);

  const getDisplayForId = useCallback(
    (id: string, fallback?: string) => {
      const mention = mentionablesMap.get(id);
      return mention?.name?.trim() || mention?.email?.trim() || fallback || id;
    },
    [mentionablesMap],
  );

  const computeRangesFromDraft = useCallback(
    (draftText: string, ids: string[]): ProtectedRange[] => {
      if (!draftText || ids.length === 0) return [];
      let cursor = 0;
      const ranges: ProtectedRange[] = [];
      for (const id of ids) {
        const label = getDisplayForId(id);
        const tokenDisplay = `@${label}`;
        const idx = draftText.indexOf(tokenDisplay, cursor);
        if (idx === -1) {
          continue;
        }
        ranges.push({ start: idx, end: idx + tokenDisplay.length, id });
        cursor = idx + tokenDisplay.length;
      }
      return ranges;
    },
    [getDisplayForId],
  );

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    const maxHeight = 240;
    const next = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${next}px`;
  }, []);

  const colorOptions: Array<string | null> = [
    null,
    '#000000', // Black
    '#EF4444', // Red
    '#3B82F6', // Blue
    '#16A34A', // Green
    '#F59E0B', // Yellow
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#14B8A6', // Teal
  ];

  const activeComment = useMemo(() => {
    if (!activeCommentId) return null;
    return comments.find((comment) => comment.id === activeCommentId) ?? null;
  }, [activeCommentId, comments]);

  const deriveMentionQuery = useCallback((value: string, cursor: number) => {
    const slice = value.slice(0, cursor);
    const atIndex = slice.lastIndexOf('@');
    if (atIndex === -1) return null;
    const tail = slice.slice(atIndex);
    if (!/^@[^\s@]*$/.test(tail)) {
      return null;
    }
    const query = tail.slice(1);
    return { query, start: atIndex };
  }, []);

  const filterMentionables = useCallback(
    (query: string | null) => {
      const lower = (query ?? '').trim().toLowerCase();
      if (!lower) {
        return mentionables;
      }
      return mentionables.filter((m) => {
        const name = (m.name || '').toLowerCase();
        const email = (m.email || '').toLowerCase();
        return name.includes(lower) || email.includes(lower);
      });
    },
    [mentionables],
  );

  const renderMentionSuggestions = (query: string | null, context: MentionContext, anchorRef: RefObject<HTMLTextAreaElement | null>) => {
    const options = filterMentionables(query).slice(0, 6);
    if (!query || options.length === 0) return null;
    const anchor = anchorRef.current;
    if (!anchor) return null;
    const rect = anchor.getBoundingClientRect();
    const style: React.CSSProperties = {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxWidth: 320,
      zIndex: 9999,
    };
    return ReactDOM.createPortal(
      <div
        style={style}
        className="max-h-48 overflow-auto rounded-lg border border-border bg-background shadow-lg"
        data-mention-suggestions="true"
      >
        {options.map((option) => {
          const display = option.name || option.email || option.id;
          return (
            <button
              key={option.id}
              type="button"
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => {
                e.preventDefault();
                selectMention(option, context);
              }}
              onClick={(e) => e.preventDefault()}
            >
              <span className="truncate">{display}</span>
              {option.email && option.email !== display ? (
                <span className="ml-2 truncate text-xs text-muted-foreground">{option.email}</span>
              ) : null}
            </button>
          );
        })}
      </div>,
      document.body,
    );
  };

  const isMentionSuggestionTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('[data-mention-suggestions="true"]'));
  }, []);

  const renderBodyWithMentions = useCallback(
    (body: string) => {
      if (!body) return null;
      const segments: React.ReactNode[] = [];
      const regex = /@user\{([^}|]+)(?:\|([^}]+))?\}/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = regex.exec(body)) !== null) {
        const [full, id, label] = match;
        if (match.index > lastIndex) {
          segments.push(body.slice(lastIndex, match.index));
        }
        const resolved = mentionablesMap.get(id);
        const display = resolved?.name?.trim() || resolved?.email?.trim() || label || id;
        segments.push(
          <span
            key={`${id}-${match.index}`}
            className="inline-flex items-center rounded font-medium text-primary hover:bg-primary/10"
            data-mention-id={id}
          >
            @{display}
          </span>,
        );
        lastIndex = match.index + full.length;
      }

      if (lastIndex < body.length) {
        segments.push(body.slice(lastIndex));
      }

      return segments;
    },
    [mentionablesMap],
  );

  type MentionContext = 'root' | 'reply' | 'edit';
  const selectMention = useCallback(
    (mention: { id: string; name?: string | null; email?: string | null }, context: MentionContext) => {
      const label = (mention.name && mention.name.trim().length > 0 ? mention.name.trim() : mention.email?.trim()) ?? mention.id;
      const textToken = `@${label}`;

      if (context === 'root') {
        if (mentionStart === null) return;
        const end = composerCursor;
        const next = `${draft.slice(0, mentionStart)}${textToken} ${draft.slice(end)}`;
        setDraft(next);
        setProtectedRanges((prev) => [...prev, { start: mentionStart, end: mentionStart + textToken.length, id: mention.id }]);
        setRootMentions((prev) => (prev.includes(mention.id) ? prev : [...prev, mention.id]));
        setActiveMentionQuery(null);
        setMentionStart(null);
        requestAnimationFrame(() => resizeTextarea(textareaRef.current));
        return;
      }
      if (context === 'reply') {
        if (replyMentionStart === null) return;
        const end = replyComposerCursor;
        const next = `${replyDraft.slice(0, replyMentionStart)}${textToken} ${replyDraft.slice(end)}`;
        setReplyDraft(next);
        setReplyProtectedRanges((prev) => [...prev, { start: replyMentionStart, end: replyMentionStart + textToken.length, id: mention.id }]);
        setReplyMentions((prev) => (prev.includes(mention.id) ? prev : [...prev, mention.id]));
        setActiveReplyMentionQuery(null);
        setReplyMentionStart(null);
        requestAnimationFrame(() => resizeTextarea(replyTextareaRef.current));
        return;
      }
      if (context === 'edit') {
        const textarea = editingTextareaRef.current;
        if (!textarea) return;
        const cursor = textarea.selectionStart ?? editingDraft.length;
        const derivedEdit = deriveMentionQuery(editingDraft, cursor);
        const insertStart = derivedEdit ? derivedEdit.start : cursor;
        const next = `${editingDraft.slice(0, insertStart)}${textToken} ${editingDraft.slice(cursor)}`;
        setEditingDraft(next);
        setEditingMentions((prev) => (prev.includes(mention.id) ? prev : [...prev, mention.id]));
        setEditingProtectedRanges((prev) => [...prev, { start: insertStart, end: insertStart + textToken.length, id: mention.id }]);
        setEditingMentionQuery(null);
        requestAnimationFrame(() => resizeTextarea(editingTextareaRef.current));
      }
    },
    [
      composerCursor,
      draft,
      mentionStart,
      replyComposerCursor,
      replyDraft,
      replyMentionStart,
      editingDraft,
      resizeTextarea,
    ],
  );
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const handleInput = () => {
      const value = textarea.value;
      const cursor = textarea.selectionStart ?? value.length;
      const range = protectedRanges.find((r) => cursor > r.start && cursor <= r.end);
      if (range) {
        const next = `${value.slice(0, range.start)}${value.slice(range.end)}`;
        setDraft(next);
        setRootMentions((prev) => prev.filter((id) => id !== range.id));
        setProtectedRanges((prev) => prev.filter((r) => r.id !== range.id));
      }
    };
    textarea.addEventListener('input', handleInput);
    return () => textarea.removeEventListener('input', handleInput);
  }, [protectedRanges]);

  useEffect(() => {
    const textarea = replyTextareaRef.current;
    if (!textarea) return;
    const handleInput = () => {
      const value = textarea.value;
      const cursor = textarea.selectionStart ?? value.length;
      const range = replyProtectedRanges.find((r) => cursor > r.start && cursor <= r.end);
      if (range) {
        const next = `${value.slice(0, range.start)}${value.slice(range.end)}`;
        setReplyDraft(next);
        setReplyMentions((prev) => prev.filter((id) => id !== range.id));
        setReplyProtectedRanges((prev) => prev.filter((r) => r.id !== range.id));
      }
    };
    textarea.addEventListener('input', handleInput);
    return () => textarea.removeEventListener('input', handleInput);
  }, [replyProtectedRanges]);

  useEffect(() => {
    const textarea = editingTextareaRef.current;
    if (!textarea) return;
    const handleInput = () => {
      const value = textarea.value;
      const cursor = textarea.selectionStart ?? value.length;
      const range = editingProtectedRanges.find((r) => cursor > r.start && cursor <= r.end);
      if (range) {
        const next = `${value.slice(0, range.start)}${value.slice(range.end)}`;
        setEditingDraft(next);
        setEditingMentions((prev) => prev.filter((id) => id !== range.id));
        setEditingProtectedRanges((prev) => prev.filter((r) => r.id !== range.id));
      }
    };
    textarea.addEventListener('input', handleInput);
    return () => textarea.removeEventListener('input', handleInput);
  }, [editingProtectedRanges]);

  useEffect(() => {
    setActiveCommentColor(activeComment?.metadata?.color ?? null);
  }, [activeComment?.metadata?.color, activeCommentId, activeComment, currentUserId]);

  useEffect(() => {
    if (!prompt) {
      setRootMentions([]);
      setActiveMentionQuery(null);
      setMentionStart(null);
    }
  }, [prompt]);

  useEffect(() => {
    setReplyMentions([]);
    setActiveReplyMentionQuery(null);
    setReplyMentionStart(null);
  }, [activeCommentId]);

  useEffect(() => {
    if (editingCommentId) {
      const existing = findCommentInTree(comments, editingCommentId);
      if (!existing) {
        setEditingCommentId(null);
        setEditingDraft('');
        setEditingMentions([]);
        setEditingProtectedRanges([]);
        setProtectedRanges([]);
      } else {
        // keep protected ranges in sync when comments change
        const ranges = computeRangesFromDraft(editingDraft, existing.mentions ?? []);
        setEditingProtectedRanges(ranges);
        setEditingMentions(existing.mentions ?? []);
      }
    }
  }, [comments, editingCommentId]);

  useEffect(() => {
    if (editingCommentId && editingTextareaRef.current) {
      resizeTextarea(editingTextareaRef.current);
      editingTextareaRef.current.focus({ preventScroll: true });
      const length = editingTextareaRef.current.value.length;
      editingTextareaRef.current.setSelectionRange(length, length);
    }
  }, [editingCommentId, resizeTextarea]);

  useEffect(() => {
    if (prompt && textareaRef.current) {
      resizeTextarea(textareaRef.current);
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [prompt, resizeTextarea]);

  const cancelPromptPlacement = useCallback(() => {
    if (!useCommentOverlayStore.getState().prompt) {
      return;
    }
    closePrompt();
    eventBus.emit('comment:placement:cancelled');
    eventBus.emit('tool:activate', { id: 'genogram-selection' });
  }, [closePrompt, eventBus]);

  useEffect(() => {
    if (!prompt) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (isMentionSuggestionTarget(event.target)) {
        return;
      }
      const container = promptContainerRef.current;
      if (container && container.contains(event.target as Node)) {
        return;
      }
      cancelPromptPlacement();
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isMentionSuggestionTarget(event.target)) {
        return;
      }
      const container = promptContainerRef.current;
      if (container && container.contains(event.target as Node)) {
        return;
      }
      cancelPromptPlacement();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [prompt, cancelPromptPlacement, isMentionSuggestionTarget]);

  useEffect(() => {
    if (activeCommentId && !activeComment) {
      clearActiveComment();
    }
  }, [activeCommentId, activeComment, clearActiveComment]);

  useEffect(() => {
    setReplyDraft('');
    if (replyTextareaRef.current) {
      resizeTextarea(replyTextareaRef.current);
      replyTextareaRef.current.focus({ preventScroll: true });
    }
  }, [activeCommentId, resizeTextarea]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (prompt) {
        closePrompt();
        eventBus.emit('comment:placement:cancelled');
        return;
      }
      if (useCommentOverlayStore.getState().activeCommentId) {
        clearActiveComment();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prompt, clearActiveComment, closePrompt]);

  const promptPosition = useMemo(() => {
    if (!prompt) return null;
    const container = stageRef.current?.container();
    if (!container) return null;
    const containerRect = container.getBoundingClientRect();
    const parentRect = container.parentElement?.getBoundingClientRect() ?? containerRect;
    return {
      left: prompt.local.x + (containerRect.left - parentRect.left),
      top: prompt.local.y + (containerRect.top - parentRect.top),
    };
  }, [prompt, stageRef]);

  const activeCommentPosition = useMemo(() => {
    if (!activeComment || !activeComment.locOnDiagram) return null;
    const stage = stageRef.current;
    if (!stage) return null;
    const container = stage.container();
    const containerRect = container.getBoundingClientRect();
    const parentRect = container.parentElement?.getBoundingClientRect() ?? containerRect;
    const scale = stage.scaleX();
    const position = stage.position();
    const stageX = activeComment.locOnDiagram.x * scale + position.x;
    const stageY = activeComment.locOnDiagram.y * scale + position.y;
    return {
      left: stageX + (containerRect.left - parentRect.left) + 14,
      top: stageY + (containerRect.top - parentRect.top) - 10,
      parentHeight: parentRect.height,
      parentWidth: parentRect.width,
    };
  }, [activeComment, stageRef]);

  const updateOverlayCoordinates = useCallback(() => {
    if (!activeCommentPosition) {
      setOverlayCoordinates(null);
      return;
    }

    if (!overlayRef.current) {
      setOverlayCoordinates({
        left: activeCommentPosition.left,
        top: activeCommentPosition.top,
      });
      return;
    }

    const { parentHeight, parentWidth, left, top } = activeCommentPosition;
    const overlayWidth = overlayRef.current.offsetWidth;
    const overlayHeight = overlayRef.current.offsetHeight;
    const margin = 16;

    const maxLeft = Math.max(parentWidth - overlayWidth - margin, margin);
    const maxTop = Math.max(parentHeight - overlayHeight - margin, margin);

    setOverlayCoordinates({
      left: Math.min(Math.max(left, margin), maxLeft),
      top: Math.min(Math.max(top, margin), maxTop),
    });
  }, [activeCommentPosition]);

  useLayoutEffect(() => {
    updateOverlayCoordinates();
  }, [
    updateOverlayCoordinates,
    activeComment,
    comments,
    editingCommentId,
    editingDraft,
    replyDraft,
    activeCommentColor,
    isColorPopoverOpen,
  ]);

  useEffect(() => {
    if (!activeCommentPosition) {
      return;
    }
    const handleResize = () => {
      updateOverlayCoordinates();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeCommentPosition, updateOverlayCoordinates]);

  const handleSubmit = async () => {
    if (!prompt) return;
    const body = draft.trim();
    if (!body) return;
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `comment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const tokenBody = retokenizeFromMentions(
      body,
      rootMentions,
      (id) => {
        const mention = mentionablesMap.get(id);
        return mention?.name?.trim() || mention?.email?.trim();
      },
    );

    await commandManager.execute(
      new CreateRootCommentCommand({
        payload: {
          id,
          body: tokenBody,
          shapeId: prompt.shapeId ?? undefined,
          locOnDiagram: prompt.diagram,
          locOnShape: prompt.locOnShape ?? null,
          mentions: rootMentions,
        },
        createComment: createRootComment,
        eventBus,
        onSuccess: () => {
          setDraft('');
          setRootMentions([]);
          setActiveMentionQuery(null);
          setMentionStart(null);
          closePrompt();
        },
        onError: (error) => {
          logger.error('Failed to create comment', error);
        },
      }),
    );
  };

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleReplySubmit = async () => {
    if (!activeComment) return;
    const body = replyDraft.trim();
    if (!body) return;
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `reply-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const previousDraft = replyDraft;
    const previousMentions = replyMentions;
    setReplyDraft('');
    setReplyMentions([]);
    try {
      const replyTokenBody = retokenizeFromMentions(
        body,
        replyMentions,
        (id) => {
          const mention = mentionablesMap.get(id);
          return mention?.name?.trim() || mention?.email?.trim();
        },
      );
      await createReply(activeComment.id, {
        id,
        body: replyTokenBody,
        mentions: replyMentions,
      });
      requestAnimationFrame(() => {
        if (!replyTextareaRef.current) {
          return;
        }
        resizeTextarea(replyTextareaRef.current);
        replyTextareaRef.current.focus({ preventScroll: true });
      });
    } catch (error) {
      logger.error('Failed to create reply', error);
      setReplyDraft(previousDraft);
      setReplyMentions(previousMentions);
      requestAnimationFrame(() => {
        if (!replyTextareaRef.current) {
          return;
        }
        resizeTextarea(replyTextareaRef.current);
        replyTextareaRef.current.focus({ preventScroll: true });
        const length = previousDraft.length;
        replyTextareaRef.current.setSelectionRange(length, length);
      });
      throw error;
    }
  };

  const handleReplyKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleReplySubmit();
    }
  };

  const beginEditing = (comment: CommentNode) => {
    setEditingCommentId(comment.id);
    const draftBody = renderTokensToDisplay(
      comment.body,
      (id, label) => {
        const mention = mentionablesMap.get(id);
        return mention?.name?.trim() || mention?.email?.trim() || label;
      },
    );
    setEditingDraft(draftBody);
    setEditingMentions(comment.mentions ?? []);
    setEditingProtectedRanges(computeRangesFromDraft(draftBody, comment.mentions ?? []));
    setProtectedRanges([]);
    setEditingMentionQuery(null);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingDraft('');
  };

  const handleEditSave = async (commentId: string) => {
    const trimmed = editingDraft.trim();
    if (!trimmed) {
      return;
    }
    const previousDraft = editingDraft;
    const previousEditingId = editingCommentId;
    setEditingCommentId(null);
    setEditingDraft('');
    try {
      const tokenized = retokenizeFromMentions(
        trimmed,
        editingMentions,
        (id) => {
          const mention = mentionablesMap.get(id);
          return mention?.name?.trim() || mention?.email?.trim();
        },
      );
      await updateComment(commentId, { body: tokenized });
    } catch (error) {
      logger.error('Failed to update comment', error);
      const targetId = previousEditingId ?? commentId;
      setEditingCommentId(targetId);
      setEditingDraft(previousDraft);
      requestAnimationFrame(() => {
        const textarea = editingTextareaRef.current;
        if (!textarea) {
          return;
        }
        resizeTextarea(textarea);
        textarea.focus({ preventScroll: true });
        const length = previousDraft.length;
        textarea.setSelectionRange(length, length);
      });
    }
  };

  const onToggleResolve = async (commentId: string, nextState: 'resolved' | 'open') => {
    if (nextState === 'resolved') {
      await resolveComment(commentId);
    } else {
      await reopenComment(commentId);
    }
  };

  const handleColorSelection = async (color: string | null) => {
    if (!activeComment) {
      return;
    }

    const currentColor = activeComment.metadata?.color ?? null;
    if (currentColor === color) {
      setActiveCommentColor(color);
      setIsColorPopoverOpen(false);
      return;
    }

    const previousColor = activeCommentColor;
    setActiveCommentColor(color);
    setIsColorPopoverOpen(false);

    const nextMetadata: CommentNode['metadata'] = {
      ...(activeComment.metadata ?? {}),
      color,
    };

    try {
      await updateComment(activeComment.id, { metadata: nextMetadata });
    } catch (error) {
      logger.error('Failed to update comment color', error);
      setActiveCommentColor(previousColor);
    }
  };

  const handleDelete = (commentId?: string) => {
    const targetId = commentId ?? activeComment?.id;
    if (!targetId) {
      return;
    }
    setPendingDeleteCommentId(targetId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    const targetId = pendingDeleteCommentId ?? activeComment?.id;
    if (!targetId) {
      return;
    }
    setShowDeleteConfirm(false);
    try {
      await softDeleteComment(targetId);
      setPendingDeleteCommentId(null);
    } catch (error) {
      logger.error('Failed to delete comment', error);
    }
  };

  return (
    <>
      {prompt && promptPosition && (
        <div
          ref={promptContainerRef}
          className="absolute z-30"
          style={{ left: promptPosition.left + 12, top: promptPosition.top + 12 }}
        >
          <div className="w-64 rounded-xl border border-border bg-background p-3 shadow-xl">
            <div className="relative flex items-end gap-2 rounded-2xl border border-input bg-background px-3 py-1.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30">
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(event) => {
                  const next = event.target.value;
                  setDraft(next);
                  const cursor = event.target.selectionStart ?? next.length;
                  setComposerCursor(cursor);
                  const derived = deriveMentionQuery(next, cursor);
                  if (derived) {
                    setActiveMentionQuery(derived.query);
                    setMentionStart(derived.start);
                  } else {
                    setActiveMentionQuery(null);
                    setMentionStart(null);
                  }
                  resizeTextarea(event.currentTarget);
                }}
                onKeyDown={handlePromptKeyDown}
                className="w-full min-h-[32px] max-h-48 resize-none overflow-hidden bg-transparent text-sm leading-5 focus:outline-none"
                placeholder={t('comments.addComment')}
                disabled={isCreating}
              />
              <Button
                type="button"
                size="icon"
                className="h-5 w-5 rounded-full"
                onClick={handleSubmit}
                disabled={isCreating || !draft.trim()}
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            {renderMentionSuggestions(activeMentionQuery, 'root', textareaRef)}
          </div>
        </div>
      )}

      {activeComment && activeCommentPosition && !prompt && (
        <div
          ref={overlayRef}
          className="absolute z-20"
          style={{
            left: (overlayCoordinates ?? activeCommentPosition).left,
            top: (overlayCoordinates ?? activeCommentPosition).top,
          }}
        >
          <TooltipProvider delayDuration={100}>
            <div className="flex h-full max-h-[80vh] w-80 max-w-[80vw] flex-col rounded-lg border border-border bg-background shadow-xl overflow-visible">
              <div className="flex items-center justify-between border-b border-border/70 px-3 py-2">
                <span className="text-sm font-semibold text-foreground">{t('comments.comment')}</span>
                <div className="flex items-center gap-1.5">
                  <Popover open={isColorPopoverOpen} onOpenChange={setIsColorPopoverOpen}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="relative h-8 w-8 rounded-full"
                            aria-label={t('comments.commentColor')}
                          >
                            <Palette className="h-4 w-4" />
                            <span
                              className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full border border-border"
                              style={{ backgroundColor: activeCommentColor ?? 'transparent' }}
                            />
                          </Button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent>{t('comments.selectCommentColor')}</TooltipContent>
                    </Tooltip>
                    <PopoverContent className="w-48 p-3" align="end">
                      <div className="grid grid-cols-5 gap-2">
                        {colorOptions.map((color) => {
                          const isSelected = color === activeCommentColor || (!color && !activeCommentColor);
                          return (
                            <button
                              key={color ?? 'none'}
                              type="button"
                              className={`h-7 w-7 rounded-full border ${
                                isSelected ? 'border-primary ring-2 ring-primary/60' : 'border-border'
                              } flex items-center justify-center`}
                              style={{ backgroundColor: color ?? 'transparent' }}
                              onClick={() => handleColorSelection(color)}
                              disabled={isUpdating}
                            >
                              {color === null && (
                                <span className="text-xs font-medium text-muted-foreground">×</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        data-testid="comment-thread-toggle-resolve"
                        onClick={() =>
                          onToggleResolve(
                            activeComment.id,
                            activeComment.state === 'resolved' ? 'open' : 'resolved',
                          )
                        }
                        disabled={activeComment.state === 'resolved' ? isReopening : isResolving}
                        aria-label={activeComment.state === 'resolved' ? t('comments.reopenComment') : t('comments.resolveComment')}
                      >
                        {activeComment.state === 'resolved' ? (
                          <RotateCcw className="h-4 w-4" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {activeComment.state === 'resolved' ? t('comments.reopenComment') : t('comments.resolveComment')}
                    </TooltipContent>
                  </Tooltip>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        aria-label={t('comments.commentOptions')}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          handleDelete();
                        }}
                        disabled={isDeleting}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('comments.deleteThread')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        aria-label={t('comments.closeOverlay')}
                        onClick={() => {
                          clearActiveComment();
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('comments.close')}</TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-1.5">
                {[activeComment, ...(activeComment.replies ?? [])].map((comment, index) => (
                  <div
                    key={comment.id}
                    className={`px-1.5 py-3 ${index === 0 ? '' : 'border-t border-border/60'}`}
                  >
                    {renderCommentCard(comment, index !== 0)}
                  </div>
                ))}
              </div>

              {isAuthenticated ? (
                <div className="border-t border-border/70 bg-background px-3 py-3">
                  <div className="relative flex items-end gap-2 rounded-2xl border border-input bg-background px-3 py-1.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30">
                    <textarea
                      ref={replyTextareaRef}
                      value={replyDraft}
                      onChange={(event) => {
                        const next = event.target.value;
                        setReplyDraft(next);
                        const cursor = event.target.selectionStart ?? next.length;
                        setReplyComposerCursor(cursor);
                        const derived = deriveMentionQuery(next, cursor);
                        if (derived) {
                          setActiveReplyMentionQuery(derived.query);
                          setReplyMentionStart(derived.start);
                        } else {
                          setActiveReplyMentionQuery(null);
                          setReplyMentionStart(null);
                        }
                        resizeTextarea(event.currentTarget);
                      }}
                      onKeyDown={handleReplyKeyDown}
                      className="w-full min-h-[32px] max-h-48 resize-none overflow-hidden bg-transparent text-sm leading-5 focus:outline-none"
                      placeholder={t('comments.writeReply')}
                      disabled={isReplying}
                    />
                    <Button
                      type="button"
                      size="icon"
                      className="h-5 w-5 rounded-full"
                      onClick={handleReplySubmit}
                      disabled={isReplying || !replyDraft.trim()}
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {renderMentionSuggestions(activeReplyMentionQuery, 'reply', replyTextareaRef)}
                </div>
              ) : null}
            </div>
          </TooltipProvider>
        </div>
      )}

      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) {
            setPendingDeleteCommentId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingDeleteCommentId === activeComment?.id
                ? t('comments.deleteThread')
                : t('comments.deleteReply', { defaultValue: 'Delete reply?' })}
            </DialogTitle>
            <DialogDescription>
              {pendingDeleteCommentId === activeComment?.id
                ? t('comments.deleteThreadConfirm')
                : t('comments.deleteReplyConfirm', { defaultValue: 'This reply will be permanently deleted.' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {t('actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  function renderCommentCard(comment: CommentNode, isReply = false): React.ReactNode {
    const author = resolveCommentAuthorVisual(
      comment.authorId ?? null,
      comment.author ?? null,
      comment.metadata?.color ?? null
    );
    const timestampLabel = formatRelativeTime(comment.createdAt, timeLocale);
    const avatarSize = isReply ? 'h-7 w-7' : 'h-8 w-8';
    const fallbackTextSize = isReply ? 'text-xs' : 'text-sm';
    const normalizedAuthorId = comment.authorId ? comment.authorId.toLowerCase() : null;
    const isOwnComment = Boolean(normalizedAuthorId) && normalizedAuthorId === currentUserId;
    const isEditing = editingCommentId === comment.id;
    const draftValue = isEditing ? editingDraft : comment.body;
    const nameClass = 'text-sm font-medium';

    return (
      <div className="flex items-start gap-3">
        <Avatar className={`${avatarSize} shrink-0`}>
          <AvatarFallback
            className={`bg-transparent font-semibold text-white ${fallbackTextSize}`}
            style={{ backgroundColor: author.color }}
          >
            {author.fallbackText}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className={nameClass}>{author.displayName}</span>
              {!isEditing ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted transition-colors"
                      aria-label={t('comments.commentMenu')}
                    >
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuItem
                      disabled={!isOwnComment}
                      onSelect={(event) => {
                        event.preventDefault();
                        if (!isOwnComment) return;
                        beginEditing(comment);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {t('comments.edit')}
                    </DropdownMenuItem>
                    {isReply && (
                      <DropdownMenuItem
                        disabled={!isOwnComment || isDeleting}
                        onSelect={(event) => {
                          event.preventDefault();
                          if (!isOwnComment) return;
                          handleDelete(comment.id);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('actions.delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="h-6 w-6" />
              )}
            </div>
            {(!isReply && comment.state === 'resolved') || timestampLabel ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {!isReply && comment.state === 'resolved' ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-emerald-700">
                    {t('comments.resolved')}
                  </span>
                ) : null}
                {timestampLabel ? <span>{timestampLabel}</span> : null}
              </div>
            ) : null}
            {isEditing ? (
              <div className="space-y-2">
                <div className="relative flex items-end gap-2 rounded-2xl border border-input bg-background px-3 py-1.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30">
                  <textarea
                    ref={editingTextareaRef}
                    value={draftValue}
                    onChange={(event) => {
                      const next = event.target.value;
                      setEditingDraft(next);
                      const cursor = event.target.selectionStart ?? next.length;
                      const derived = deriveMentionQuery(next, cursor);
                      if (derived) {
                        setEditingMentionQuery(derived.query);
                      } else {
                        setEditingMentionQuery(null);
                      }
                      resizeTextarea(event.currentTarget);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelEditing();
                        return;
                      }
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void handleEditSave(comment.id);
                        return;
                      }
                    }}
                    className="w-full min-h-[32px] max-h-48 resize-none overflow-hidden bg-transparent text-sm focus:outline-none"
                    disabled={isUpdating}
                  />
                  {renderMentionSuggestions(editingMentionQuery, 'edit', editingTextareaRef)}
                  <Button
                    type="button"
                    size="icon"
                    className="h-5 w-5 rounded-full"
                    onClick={() => { void handleEditSave(comment.id); }}
                    disabled={isUpdating || draftValue.trim().length === 0 || draftValue === comment.body}
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={isUpdating}>
                    {t('comments.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={`whitespace-pre-wrap break-words text-foreground ${isReply ? 'text-sm' : 'text-[0.95rem]'}`}>
                {renderBodyWithMentions(comment.body)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};
